import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";

const API = "https://eps.elkomprintama.com/";

const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400";
const lbl =
  "block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1";
const TYPE_OPTIONS = ["PT", "CV", "Other"];

function EditModal({ customer, onClose, onSaved }) {
  const isStandard = TYPE_OPTIONS.slice(0, 2).includes(customer.cust_type);
  const [typeChoice, setTypeChoice] = useState(
    isStandard ? customer.cust_type : "Other",
  );
  const [customType, setCustomType] = useState(
    isStandard ? "" : customer.cust_type,
  );
  const [custName, setCustName] = useState(customer.cust_name);
  const [custPhone, setCustPhone] = useState(customer.cust_phone || "");
  const [custAddr, setCustAddr] = useState(customer.cust_addr || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!custName.trim()) {
      toast.warning("Customer name is required.");
      return;
    }
    const cust_type = typeChoice === "Other" ? customType.trim() : typeChoice;
    setSaving(true);
    try {
      await axios.patch(
        `${API}patch/api/customer/${customer.cust_id}`,
        {
          cust_type,
          cust_name: custName.trim(),
          cust_phone: custPhone.trim(),
          cust_addr: custAddr.trim(),
        },
        { withCredentials: true },
      );
      toast.success("Customer updated!");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to update.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-800">Edit Customer</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
          <div>
            <label className={lbl}>Type</label>
            <div className="flex gap-2">
              {TYPE_OPTIONS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTypeChoice(t)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold border transition ${
                    typeChoice === t
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-600 border-gray-300 hover:border-blue-400"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            {typeChoice === "Other" && (
              <input
                className={`${inp} mt-2`}
                name="customtype" type="text"
                placeholder="Enter type (e.g. UD, Toko...)"
                value={customType}
                onChange={(e) => setCustomType(e.target.value)}
              />
            )}
          </div>

          <div>
            <label className={lbl}>
              Customer Name <span className="text-red-400">*</span>
            </label>
            <input
              className={inp}
              name="custname" type="text"
              value={custName}
              onChange={(e) => setCustName(e.target.value)}
            />
          </div>

          <div>
            <label className={lbl}>Contact No.</label>
            <input
              className={inp}
              name="custphone" type="text"
              value={custPhone}
              onChange={(e) => setCustPhone(e.target.value)}
            />
          </div>

          <div>
            <label className={lbl}>Address</label>
            <textarea
              className={`${inp} resize-none`}
              rows={3}
              value={custAddr}
              onChange={(e) => setCustAddr(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CustomerManagement() {
  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}get/api/customers`, {
        withCredentials: true,
      });
      setCustomers(res.data.result || []);
    } catch {
      toast.error("Failed to load customers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      customers.filter((c) =>
        `${c.cust_type} ${c.cust_name} ${c.cust_phone || ""} ${c.cust_addr || ""}`
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [search, customers]);

  const thCls =
    "px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200";
  const tdCls = "px-4 py-2.5 text-sm border-b border-gray-100";

  return (
    <>
      <div className="w-full rounded-lg bg-blue-600 mb-4">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Customer List
        </label>
      </div>

      <div
        className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden relative flex flex-col"
        style={{ maxHeight: "calc(100vh - 160px)" }}
      >
        {loading && <LoadingOverlay message="Loading customers..." />}

        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <input
            type="text" autoComplete="off"
            placeholder="Search by name, type, phone, address..."
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="text-xs text-gray-400 whitespace-nowrap">
            {filtered.length} customer{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className={thCls}>No</th>
                <th className={thCls}>Type</th>
                <th className={thCls}>Customer Name</th>
                <th className={thCls}>Contact No.</th>
                <th className={thCls}>Address</th>
                <th className={`${thCls} text-center`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-12 text-gray-400 italic text-sm"
                  >
                    {search
                      ? "No customers match your search."
                      : "No customers found."}
                  </td>
                </tr>
              ) : (
                filtered.map((c, i) => (
                  <tr
                    key={c.cust_id}
                    className={
                      i % 2 === 0
                        ? "bg-white hover:bg-blue-50"
                        : "bg-gray-50 hover:bg-blue-50"
                    }
                  >
                    <td className={`${tdCls} text-gray-400 w-12`}>{i + 1}</td>
                    <td className={`${tdCls} w-16`}>
                      <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-100 text-blue-700">
                        {c.cust_type || "—"}
                      </span>
                    </td>
                    <td className={`${tdCls} font-medium text-gray-800`}>
                      {c.cust_name}
                    </td>
                    <td className={`${tdCls} text-gray-500`}>
                      {c.cust_phone || "—"}
                    </td>
                    <td className={`${tdCls} text-gray-500 max-w-xs truncate`}>
                      {c.cust_addr || "—"}
                    </td>
                    <td className={`${tdCls} text-center`}>
                      <button
                        onClick={() => setEditing(c)}
                        className="px-3 py-1 rounded-lg text-xs font-semibold text-blue-600 border border-blue-300 hover:bg-blue-600 hover:text-white transition"
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditModal
          customer={editing}
          onClose={() => setEditing(null)}
          onSaved={fetchCustomers}
        />
      )}
    </>
  );
}

export default CustomerManagement;
